import { TaxType } from '../types/catering/cateringOrderTax.enum';
import { formatNumber } from './utils';

interface CateringOrderItemAmountInput {
  quantity: number;
  unitPrice: number;
}

interface CateringOrderChargeAmountInput {
  type: TaxType | string;
  value: number;
}

interface CalculateCateringOrderAmountsInput {
  items?: CateringOrderItemAmountInput[];
  taxes?: CateringOrderChargeAmountInput[];
  serviceCharges?: CateringOrderChargeAmountInput[];
}

interface CalculatedCateringOrderAmounts {
  subtotal: number;
  totalTax: number;
  totalServiceCharge: number;
  totalAmount: number;
}

const toNumber = (value: number): number => (Number.isFinite(value) ? value : 0);
export const formatCurrency = (value: number): number => formatNumber(value, 2) ?? 0;

const calculateChargeAmount = (value: number, type: TaxType | string, base: number): number => {
  const numericValue = toNumber(value);
  return type === TaxType.PERCENTAGE ? (base * numericValue) / 100 : numericValue;
};

/**
 * Calculates catering order subtotal, tax, service charge and grand total.
 * Service charges with `percentage` type are computed against subtotal.
 * Taxes with `percentage` type are computed against (subtotal + service charge).
 */
export const calculateCateringOrderAmounts = (
  input: CalculateCateringOrderAmountsInput
): CalculatedCateringOrderAmounts => {
  const subtotalRaw = (input.items ?? []).reduce(
    (acc, item) => acc + toNumber(item.quantity) * toNumber(item.unitPrice),
    0
  );
  const subtotal = formatCurrency(subtotalRaw);

  const totalServiceChargeRaw = (input.serviceCharges ?? []).reduce(
    (acc, charge) => acc + calculateChargeAmount(charge.value, charge.type, subtotal),
    0
  );
  const totalServiceCharge = formatCurrency(totalServiceChargeRaw);
  const taxableAmount = formatCurrency(subtotal + totalServiceCharge);

  const totalTaxRaw = (input.taxes ?? []).reduce(
    (acc, tax) => acc + calculateChargeAmount(tax.value, tax.type, taxableAmount),
    0
  );
  const totalTax = formatCurrency(totalTaxRaw);

  return {
    subtotal,
    totalTax,
    totalServiceCharge,
    totalAmount: formatCurrency(taxableAmount + totalTax),
  };
};

export type {
  CalculateCateringOrderAmountsInput,
  CalculatedCateringOrderAmounts,
  CateringOrderChargeAmountInput,
  CateringOrderItemAmountInput,
};
